import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '../../../../../services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { TextEditorComponent, TextEditorStyle } from '../../../../../shared/components/text-editor';
import { PackageService } from '../../../../../services/package.service';
import { Package } from '../../../../../shared/types';
import { TemplateService } from '../../../../../car-wash-website/services/template.service';
import { NotificationService } from '../../../../../services/notification.service'; // NotificationService;
import { PACKAGE_ICON_FALLBACK } from '../../../../../shared/constants/website-builder-assets';

export interface WashPackagePanelData {
  title?: string;
  subtitle?: string;
  titleStyle?: TextEditorStyle;
  theme?: 'light' | 'dark';
  backgroundColor?: string;
  packages: WashPackageItem[];
}

export interface WashPackageItem {
  packageId: string;
  packageName: string;
  imageUrl: string;
  imageFit: 'fill' | 'cover' | 'contain' | 'fit';
  buttonText: string;
}

@Component({
  selector: 'app-wash-packages-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ColorPickerModule, TextEditorComponent],
  templateUrl: './wash-packages-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './wash-packages-panel.component.scss',
})
export class WashPackagesPanelComponent implements OnInit, OnChanges {
  readonly packageIconFallback = PACKAGE_ICON_FALLBACK;

  @Input() data: WashPackagePanelData = {
    title: '',
    subtitle: '',
    theme: 'light',
    backgroundColor: '#FFFFFF',
    packages: [],
  };
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<WashPackagePanelData>();
  @Output() dataChange = new EventEmitter<WashPackagePanelData>();

  activeTab: 'content' | 'layout' = 'content';
  expandedSections: Record<string, boolean> = {
    heading: true,
    theme: false,
    background: false,
  };
  packages: Package[] = [];
  loading = false;
  selectedTheme: 'light' | 'dark' = 'light';

  customColors: string[] = [];

  // Color picker state
  isColorPickerOpen = false;
  currentPickerColor: string | any = '#000000';

  // Image upload state
  uploadingImages: Record<string, boolean> = {};
  uploadErrors: Record<string, string | null> = {};

  // Track if packages have been synced initially to prevent overwriting user changes
  private packagesSynced = false;

  private static readonly PREDEFINED_COLORS = ['#5E19A1', '#FF9736', '#079271', '#1B5BFF'];

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private templateService: TemplateService,
    private cdr: ChangeDetectorRef,
    private messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadPackages();
    // Initialize theme from data
    if (this.data.theme) {
      this.selectedTheme = this.data.theme;
    }
    // Load custom color on initial load
    this.loadCustomColorFromData();
  }

  /**
   * Handle Angular input changes: syncs or updates packages and loads custom background color based on incoming data.
   * @example
   * gOnChanges({ data: { packageSectionPackages: [...] } })
   * undefined
   * @param {SimpleChanges} changes - Angular SimpleChanges containing input property changes.
   * @returns {void} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (this.data) {
        // Check if packageSectionPackages has changed (new data loaded from API)
        const newPackageSectionPackages = (this.data as any).packageSectionPackages || [];
        const previousPackageSectionPackages =
          (changes['data'].previousValue as any)?.packageSectionPackages || [];

        // If packageSectionPackages changed, it means new data was loaded, so we should sync
        const packageSectionPackagesChanged =
          JSON.stringify(newPackageSectionPackages) !==
          JSON.stringify(previousPackageSectionPackages);

        // Only sync packages on initial load or when packageSectionPackages changed
        // This prevents overwriting user changes with old saved data, but allows loading new saved data
        const shouldSync =
          !this.packagesSynced || changes['data'].firstChange || packageSectionPackagesChanged;

        if (shouldSync) {
          this.syncPackagesWithData();
          this.packagesSynced = true;
        } else {
          // On subsequent changes, only add missing packages, don't overwrite existing buttonText
          this.addMissingPackagesOnly();
        }
      }
      if (this.data?.backgroundColor) {
        // Load custom color if backgroundColor is not in predefined colors
        this.loadCustomColorFromData();
      }
    }
  }

  /**
   * Load car wash packages from the server and update component state, syncing or adding missing packages as needed.
   * @example
   * this.loadPackages()
   * void
   * @returns {void} Updates this.loading, sets this.packages, triggers sync or adds missing packages, and handles errors.
   **/
  private loadPackages(): void {
    this.loading = true;
    this.packageService.getCarWashPackages().subscribe({
      next: response => {
        if (response.status && response.data) {
          this.packages = response.data;
          // Only sync if packages haven't been synced yet (to avoid overwriting user changes)
          // The sync will happen in ngOnChanges when data is set
          if (!this.packagesSynced) {
            this.syncPackagesWithData();
          } else {
            // Just add missing packages without overwriting existing buttonText
            this.addMissingPackagesOnly();
          }
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('Error loading packages:', err);
        this.loading = false;
      },
    });
  }

  /**
   * Synchronizes the component's internal package data with incoming API packages and saved package settings: adds missing packages, updates packageName, imageUrl, imageFit and buttonText from API or saved settings, and emits a data change event on the initial sync.
   * @example
   * syncPackagesWithData()
   * undefined
   * @returns {void} No return value.
   **/
  private syncPackagesWithData(): void {
    if (!this.packages.length) return;

    const existingPackageIds = new Set(this.data.packages.map(p => p.packageId));

    // Add missing packages from API
    this.packages.forEach(pkg => {
      if (!existingPackageIds.has(pkg.id)) {
        // Use API image if available, otherwise empty string (will show placeholder)
        const apiImage = pkg.image && pkg.image.trim() !== '' ? pkg.image : '';
        // Check if package has imageFit/scaletype in API data
        const apiImageFit = (pkg as any).imageFit || (pkg as any).scaletype || 'fill';
        // Map API scaletype to panel imageFit (fill, cover, contain, fit)
        let imageFit: 'fill' | 'cover' | 'contain' | 'fit' = 'fill';
        if (apiImageFit === 'cover' || apiImageFit === 'contain' || apiImageFit === 'fit') {
          imageFit = apiImageFit;
        } else if (apiImageFit === 'stretch') {
          imageFit = 'fill';
        }
        this.data.packages.push({
          packageId: pkg.id,
          packageName: pkg.package_name || '',
          imageUrl: apiImage, // Use API image if available
          imageFit: imageFit,
          buttonText: (pkg as any).buttonText || 'Buy Now',
        });
      }
    });

    // Update package names, images, and imageFit if they changed in API
    // First, try to get settings from package_section.packages (saved settings)
    const packageSectionPackages = (this.data as any).packageSectionPackages || [];

    this.data.packages.forEach(pkgData => {
      const apiPackage = this.packages.find(p => p.id === pkgData.packageId);
      if (apiPackage) {
        pkgData.packageName = apiPackage.package_name || pkgData.packageName;
        // Update imageUrl from API if it's currently empty and API has an image
        if (!pkgData.imageUrl && apiPackage.image && apiPackage.image.trim() !== '') {
          pkgData.imageUrl = apiPackage.image;
        }
      }

      // Check package_section.packages for saved imageFit and buttonText
      const savedPackageConfig = packageSectionPackages.find(
        (p: any) => p.id === pkgData.packageId
      );
      if (savedPackageConfig) {
        // Update imageFit from saved settings
        if (savedPackageConfig.scaletype) {
          let imageFit: 'fill' | 'cover' | 'contain' | 'fit' = 'fill';
          const scaletype = savedPackageConfig.scaletype;
          if (scaletype === 'cover' || scaletype === 'contain' || scaletype === 'fit') {
            imageFit = scaletype;
          } else if (scaletype === 'stretch') {
            imageFit = 'fill';
          }
          pkgData.imageFit = imageFit;
        }
        // Update buttonText from saved settings
        if (savedPackageConfig.buttonText) {
          pkgData.buttonText = savedPackageConfig.buttonText;
        }
      } else {
        // Fallback: check API package directly
        if (apiPackage) {
          const apiImageFit = (apiPackage as any).imageFit || (apiPackage as any).scaletype;
          if (apiImageFit) {
            let imageFit: 'fill' | 'cover' | 'contain' | 'fit' = 'fill';
            if (apiImageFit === 'cover' || apiImageFit === 'contain' || apiImageFit === 'fit') {
              imageFit = apiImageFit;
            } else if (apiImageFit === 'stretch') {
              imageFit = 'fill';
            }
            pkgData.imageFit = imageFit;
          }
          // Update buttonText from API if available
          if ((apiPackage as any).buttonText) {
            pkgData.buttonText = (apiPackage as any).buttonText;
          }
        }
      }
    });

    // Only emit data change on initial sync to avoid overwriting user changes
    if (!this.packagesSynced) {
      this.emitDataChange();
    }
  }

  // Add missing packages only, without overwriting existing buttonText
  /**
   * Add missing packages from this.packages into this.data.packages, mapping API image/scaletype to panel imageFit and preserving any saved package settings.
   * @example
   * this.addMissingPackagesOnly()
   * void
   * @returns {void} Updates this.data.packages in place; does not return a value.
   **/
  private addMissingPackagesOnly(): void {
    if (!this.packages.length) return;

    const existingPackageIds = new Set(this.data.packages.map(p => p.packageId));

    // Add missing packages from API
    this.packages.forEach(pkg => {
      if (!existingPackageIds.has(pkg.id)) {
        // Use API image if available, otherwise empty string (will show placeholder)
        const apiImage = pkg.image && pkg.image.trim() !== '' ? pkg.image : '';
        // Check if package has imageFit/scaletype in API data
        const apiImageFit = (pkg as any).imageFit || (pkg as any).scaletype || 'fill';
        // Map API scaletype to panel imageFit (fill, cover, contain, fit)
        let imageFit: 'fill' | 'cover' | 'contain' | 'fit' = 'fill';
        if (apiImageFit === 'cover' || apiImageFit === 'contain' || apiImageFit === 'fit') {
          imageFit = apiImageFit;
        } else if (apiImageFit === 'stretch') {
          imageFit = 'fill';
        }

        // Check saved settings for this new package
        const packageSectionPackages = (this.data as any).packageSectionPackages || [];
        const savedPackageConfig = packageSectionPackages.find((p: any) => p.id === pkg.id);

        this.data.packages.push({
          packageId: pkg.id,
          packageName: pkg.package_name || '',
          imageUrl: apiImage,
          imageFit: savedPackageConfig?.scaletype
            ? savedPackageConfig.scaletype === 'cover' ||
              savedPackageConfig.scaletype === 'contain' ||
              savedPackageConfig.scaletype === 'fit'
              ? savedPackageConfig.scaletype
              : 'fill'
            : imageFit,
          buttonText: savedPackageConfig?.buttonText || (pkg as any).buttonText || 'Buy Now',
        });
      }
    });
  }

  // Load custom color from data if it's not a predefined color
  /**
   * Loads a custom background color from the component data and updates this.customColors accordingly.
   * @example
   * this.loadCustomColorFromData()
   * // No return value; updates this.customColors to [bgColor] if data.backgroundColor is custom, otherwise []
   * @param {void} none - No arguments expected.
   * @returns {void} No return value.
   */
  private loadCustomColorFromData(): void {
    if (!this.data?.backgroundColor) {
      return;
    }

    const bgColor = this.data.backgroundColor;

    // Check if the color is not in predefined colors
    if (!WashPackagesPanelComponent.PREDEFINED_COLORS.includes(bgColor)) {
      // Add to custom colors (maximum 1 custom color)
      this.customColors = [bgColor];
    } else {
      // If it's a predefined color, clear custom colors
      this.customColors = [];
    }
  }

  onClose(): void {
    this.close.emit();
  }

  switchTab(tab: 'content' | 'layout'): void {
    this.activeTab = tab;
  }

  /**
   * Toggle expansion state for a given panel section, collapsing others when opening standard sections.
   * @example
   * toggleSection('theme')
   * undefined
   * @param {{string}} {{section}} - The key/name of the section to toggle (e.g., 'heading', 'theme', 'background').
   * @returns {{void}} No return value; updates the component's expandedSections state.
   **/
  toggleSection(section: string): void {
    const wasExpanded = this.expandedSections[section] || false;

    if (wasExpanded) {
      // If clicking an expanded section, just collapse it
      this.expandedSections[section] = false;
    } else {
      // If clicking a collapsed section, close all others first, then expand this one
      // Only do this for standard sections (heading, theme, background)
      if (section === 'heading' || section === 'theme' || section === 'background') {
        Object.keys(this.expandedSections).forEach(key => {
          if (key === 'heading' || key === 'theme' || key === 'background') {
            this.expandedSections[key] = false;
          }
        });
      }
      this.expandedSections[section] = true;
    }
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSections[section] || false;
  }

  onTitleChange(value: string): void {
    this.data.title = value;
    this.emitDataChange();
  }

  onTitleStyleChange(style: TextEditorStyle): void {
    this.data.titleStyle = style;
    this.emitDataChange();
  }

  onSubtitleChange(value: string): void {
    this.data.subtitle = value;
    this.emitDataChange();
  }

  selectTheme(theme: 'light' | 'dark'): void {
    this.selectedTheme = theme;
    this.data.theme = theme;
    this.emitDataChange();
  }

  onBackgroundColorChange(color: string): void {
    this.data.backgroundColor = color;
    this.emitDataChange();
  }

  onAddColor(): void {
    // Open color picker modal
    this.openColorPicker();
  }

  // Open color picker for custom color
  openColorPicker(color?: string): void {
    this.currentPickerColor = color || this.data.backgroundColor || '#000000';
    this.isColorPickerOpen = true;
  }

  // Close color picker and add the selected color to custom colors list
  /**
   * Close the color picker, apply the selected color to the background, and add it to custom colors.
   * @example
   * loseColorPicker()
   * undefined
   * @returns {void} Closes the color picker, applies the currently selected color (ensuring a leading '#'), and stores it as a custom color.
   **/
  closeColorPicker(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Apply the color immediately
      this.onBackgroundColorChange(hexColor);

      // Add to custom colors (maximum 1 custom color - replace if exists)
      this.addCustomColor(hexColor);
    }
    this.isColorPickerOpen = false;
  }

  // Handle color change from color picker (update preview only, don't add to list yet)
  /**
   * Normalize and apply a color selected from a color picker: converts various color formats to a hex string (ensures leading '#'), updates this.currentPickerColor, and calls onBackgroundColorChange for immediate preview.
   * @example
   * nColorPickerChange()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} No return value; updates component state and triggers a preview update.
   **/
  onColorPickerChange(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Update currentPickerColor to the hex string for consistency
      this.currentPickerColor = hexColor;

      // Apply the color immediately for preview (but don't add to custom colors list yet)
      this.onBackgroundColorChange(hexColor);
    }
  }

  private addCustomColor(color: string): void {
    // Maximum 1 custom color - replace existing if any
    if (!WashPackagesPanelComponent.PREDEFINED_COLORS.includes(color)) {
      // Clear existing custom colors and add the new one
      this.customColors = [color];
    }
  }

  getPackageData(packageId: string): WashPackageItem | undefined {
    return this.data.packages.find(p => p.packageId === packageId);
  }

  // Get package image: API image > uploaded image > placeholder
  /**
   * Returns the image URL for a package, preferring an uploaded image, then an API image, and finally a default placeholder.
   * @example
   * etPackageImage('pkg123') — returns uploaded URL, API image, or `PACKAGE_ICON_FALLBACK`
   * @param {{string}} {{packageId}} - Package identifier to retrieve the image URL for.
   * @returns {{string}} Returns the image URL from uploaded data, API data, or a default placeholder.
   **/
  getPackageImage(packageId: string): string {
    const pkgData = this.getPackageData(packageId);
    const apiPackage = this.packages.find(p => p.id === packageId);

    // Priority 1: Use uploaded image if available
    if (pkgData?.imageUrl && pkgData.imageUrl.trim() !== '') {
      return pkgData.imageUrl;
    }

    // Priority 2: Use API image if available
    if (apiPackage?.image && apiPackage.image.trim() !== '') {
      return apiPackage.image;
    }

    // Priority 3: Fall back to placeholder
    return PACKAGE_ICON_FALLBACK;
  }

  /**
   * Determine whether a package has an associated image (either uploaded data or API record).
   * @param packageId - The package identifier to check for an image.
   * @returns True if the package has an image URL from uploaded data or the API package record; otherwise false.
   */
  hasPackageImage(packageId: string): boolean {
    const pkgData = this.getPackageData(packageId);
    const apiPackage = this.packages.find(p => p.id === packageId);

    // Check uploaded image
    if (pkgData?.imageUrl && pkgData.imageUrl.trim() !== '') {
      return true;
    }

    // Check API image
    if (apiPackage?.image && apiPackage.image.trim() !== '') {
      return true;
    }

    return false;
  }

  onPackageImageChange(packageId: string, imageUrl: string): void {
    const pkgData = this.getPackageData(packageId);
    if (pkgData) {
      pkgData.imageUrl = imageUrl;
      this.emitDataChange();
    }
  }

  onPackageImageFitChange(packageId: string, imageFit: 'fill' | 'cover' | 'contain' | 'fit'): void {
    const pkgData = this.getPackageData(packageId);
    if (pkgData) {
      pkgData.imageFit = imageFit;
      this.emitDataChange();
    }
  }

  onPackageButtonTextChange(packageId: string, buttonText: string): void {
    const pkgData = this.getPackageData(packageId);
    if (pkgData) {
      pkgData.buttonText = buttonText;
      this.emitDataChange();
    }
  }

  /**
   * Open a file picker to select an image or video for a wash package, validate file size (<=5MB), and upload it.
   * @example
   * nReplaceImage("package_123")
   * undefined
   * @param {string} packageId - The identifier of the wash package to update the image for.
   * @returns {void} No return value.
   **/
  onReplaceImage(packageId: string): void {
    // Create file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/jpg,image/gif,video/mp4';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          this.messageService.add({
            severity: 'warn',
            summary: 'File Too Large',
            detail: 'File size must not exceed 5MB.',
          });
          return;
        }

        // Upload file to server
        this.handleFileUpload(packageId, file);
      }
    };
    input.click();
  }

  /**
   * Upload a package image, show a temporary preview immediately, and update the package data and UI with the uploaded image URL.
   * @example
   * handleFileUpload('packageId123', file)
   * void
   * @param {{string}} {{packageId}} - ID of the package to which the image belongs.
   * @param {{File}} {{file}} - Image file to upload and preview.
   * @returns {{void}} Does not return a value; performs the upload, updates previews, handles errors, and triggers change detection.
   **/
  private handleFileUpload(packageId: string, file: File): void {
    // Show loading state
    this.uploadingImages[packageId] = true;
    this.uploadErrors[packageId] = null;

    // Create temporary preview URL for immediate feedback
    const tempPreviewUrl = URL.createObjectURL(file);
    this.onPackageImageChange(packageId, tempPreviewUrl);

    // Upload directly to package endpoint
    this.templateService.uploadPackageImage(packageId, file).subscribe({
      next: response => {
        // Extract image URL from response
        let imageUrl = '';
        if (response?.data?.imageUrl) {
          imageUrl = response.data.imageUrl;
        } else if (response?.imageUrl) {
          imageUrl = response.imageUrl;
        } else if (typeof response === 'string') {
          imageUrl = response;
        }

        if (imageUrl) {
          // Replace temporary URL with uploaded URL
          URL.revokeObjectURL(tempPreviewUrl);

          // Update the package data immediately
          this.onPackageImageChange(packageId, imageUrl);

          // Also update the API package array so it reflects the new image
          // This ensures the image shows immediately without needing to reload
          const apiPackage = this.packages.find(p => p.id === packageId);
          if (apiPackage) {
            apiPackage.image = imageUrl;
          }

          // Force change detection to update the UI immediately
          this.cdr.detectChanges();
        } else {
          this.logger.error('No image URL in response:', response);
          this.uploadErrors[packageId] = 'Failed to get image URL from server';
        }

        this.uploadingImages[packageId] = false;
      },
      error: error => {
        this.logger.error('Error uploading package image:', error);
        this.uploadErrors[packageId] = 'Failed to upload image. Please try again.';
        this.uploadingImages[packageId] = false;
        // Keep the temporary preview on error
      },
    });
  }

  private emitDataChange(): void {
    this.dataChange.emit({ ...this.data });
  }

  onSave(): void {
    this.save.emit({ ...this.data });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
